/*@flow*/
import type { Element } from 'react'; /** @jsx jsx */
import { css, jsx } from '@emotion/core';
import { useContext, useState, useEffect } from 'react';
import MetricsContext from './MetricsContext';
import useStoreState from 'lib/useStoreState';
import PATHS from 'const/paths';
import apolloLocalClient from 'store/apollo-local-client';
import {
  PlaceMetricSchema,
  GET_PLACEMETRICS_ORDER,
  GET_STARTUP,
  CREATE_METRIC,
  UPDATE_METRIC,
  DESTROY_METRIC,
  UPDATE_SELECTED_METRICS,
  UPDATE_METRIC_ORDER,
} from './definitions';
import resolvers from './resolvers';
import commands from './commands';
import EmptyObject from 'lib/EmptyObject';
import { useMutation } from '@apollo/react-hooks';
import useCachedQuery from 'lib/useCachedQuery';
import hash from 'lib/hash';
import EmptyArray from 'lib/EmptyArray';
import metricsConfig from './config';
import UIContainer from './UIContainer';

export default (): Element<string> => {
  // Get app context.
  const ctx = useContext(MetricsContext);

  // Watch the areaPolygon in the global store. The global store is managed by
  // redux and it's used for sharing state between components.
  const { areaPolygon } = useStoreState({
    areaPolygon: PATHS.areaPolygonGeoJSON,
  });
  // Initialize and load the apollo local client at key 'apollo:cache'.
  // We use its component parts later on to connect the local state
  // and persisted state of the app.
  const [{ cache, client: apolloClient, persistor }] = useState(
    apolloLocalClient({
      typeDefs: PlaceMetricSchema,
      resolvers,
      key: 'apollo:cache',
    })
  );

  const opts = { client: apolloClient };

  // Get the placeMetricsOrder from persisted state.
  const {
    data: { placeMetricsOrder },
  } = useCachedQuery(GET_PLACEMETRICS_ORDER, opts);

  // These are mutation handlers bound to the apollo client in the context of
  // the component. The reason of creating the here and seeding them to context
  // is useMutation hook can only be used inside a React component. In order to
  // use them in the command blocks, we need them to be in the context.
  const [createPlaceMetric] = useMutation(CREATE_METRIC, opts);
  const [updatePlaceMetric] = useMutation(UPDATE_METRIC, opts);
  const [destroyPlaceMetric] = useMutation(DESTROY_METRIC, opts);
  const [updateSelectedMetrics] = useMutation(UPDATE_SELECTED_METRICS, opts);
  const [updatePlaceMetricsOrder] = useMutation(UPDATE_METRIC_ORDER, opts);

  // Initialize the handlers.
  useEffect(() => {
    commands(ctx).set('handlers', {
      createPlaceMetric,
      updatePlaceMetric,
      destroyPlaceMetric,
      updateSelectedMetrics,
      updatePlaceMetricsOrder,
    });
  }, []);

  // There are two queries we make to the persisted state:
  //  - App configuration. This includes:
  //    - Available place metrics
  //    - Place metric analyses supporting the place metrics.
  //  - Order of place metric panels.
  //
  // Get the config from persisted state.
  const { data: config, error: configError } = useCachedQuery(
    GET_STARTUP,
    opts
  );

  // Initialize the app data using the config from persisted state storage.
  useEffect(() => {
    if (EmptyObject.is(config)) return;
    if (configError) {
      commands(ctx).serverError(configError);
    } else {
      commands(ctx).init(config);
    }
  }, [config.me, configError]);

  // When the areaPolygon changes, update the geojson in the local state.
  // This will create new analyses for the intersected geometry, while
  // backing up the old ones, if the geojson is non-empty. Or it will restore
  // the old analyses from before the intersection was created, if the
  // geojson is empty.
  useEffect(() => {
    commands(ctx).setGeoJSON(areaPolygon);
  }, [hash(areaPolygon)]);

  // When placeMetricsOrder changes in persisted state, update
  // placeMetrics and placeMetricsOrder in the local state.
  useEffect(() => {
    if (!placeMetricsOrder) return;
    if (placeMetricsOrder.length === 0) return;
    const newPlaceMetrics = placeMetricsOrder
      .map((id) => ctx.state.placeMetrics.find((v) => v.id === id))
      .filter(Boolean);
    commands(ctx).set('placeMetrics', newPlaceMetrics);
    commands(ctx).set('placeMetricsOrder', placeMetricsOrder);
  }, [hash(placeMetricsOrder)]);

  // When placeMetrics change in state, update placeMetricsOrder in
  // persisted state.
  useEffect(() => {
    if (EmptyArray.is(ctx.state.placeMetrics)) return;
    const ids = ctx.state.placeMetrics.map((v) => v.id);
    updatePlaceMetricsOrder({ variables: { input: { ids } } });
  }, [hash(ctx.state.placeMetrics)]);

  // When geojson changes in local state, applyIntersection or
  // create a new map from mapConfig in the local state.
  useEffect(() => {
    const { mapConfig } = ctx.state;
    if (EmptyObject.is(mapConfig)) return;

    if (ctx.state.geojson) {
      commands(ctx).applyIntersection();
    } else {
      commands(ctx).createMap(mapConfig);
    }
  }, [hash(ctx.state.geojson)]);

  // When the component is first loaded, restore the local state of the app
  // from persisted state. If the version of the app in persisted state is
  // newer than the default config, use the persisted state config. Otherwise
  // use the default config as the initial state of the app.
  useEffect(() => {
    persistor.restore().then(() => {
      // Default app state.
      let c = metricsConfig;
      try {
        // Try to read statup data from persisted state.
        const data = cache.readQuery({ query: GET_STARTUP });
        // Default version of the data is 0. Use that if it's missing in data.
        const version = data.version || 0;
        // Compare versions of the data. Overwrite the default state
        // if the version number of local state is same or greater than
        // the version of the default app state or if the default state
        // does not have a version number or is at version 0.

        const overwriteDefaultConfig =
          !c.version || (c.version && c.version <= version);
        if (overwriteDefaultConfig) {
          c = data;
        }

        if (c.version > version) {
          cache.writeData({});
        }
      } catch (e) {
        // Because the app threw an error when we tried to read this data from
        // persisted state, we know that the data is missing in the
        // persisted state. Hence, we write the default app state, i.e.
        // metricsConfig, into persisted state
        cache.writeData({ data: metricsConfig });
      }
      // Initialize the app with startup data.
      commands(ctx).init(c);
    });
  }, []);

  // When mapConfig changes create a new map. This map will be for all the widgets
  // that we have enabled on the panel. Using the query of thoese configurations
  // we will get the value of each widget.
  useEffect(() => {
    const { mapConfig } = ctx.state;
    if (EmptyObject.is(mapConfig)) return;
    if (mapConfig.analyses.length === 0) return;
    const taskExecution = commands(ctx).createMap(mapConfig);
    return () => {
      taskExecution.cancel();
    };
  }, [hash(ctx.state.mapConfig)]);

  // When placeMetrics change, update the mapConfig.
  useEffect(() => {
    commands(ctx).updateMapConfig();
  }, [hash(ctx.state.placeMetrics)]);

  return (
    <div
      css={css(`
        margin-top: 1px;
        background: white;
        height: 100%;
        overflow: hidden;
      `)}
    >
      <UIContainer />
    </div>
  );
};
